require('./brokerMyInvestmentsOthersTable.scss');

// @vendors
const React = require('react');
const PropTypes = require('prop-types');
const Immutable = require('immutable');
const moment = require('moment');

// @ commons
const { OKTable } = require('commonsComponents/OKTable');
const { OKTableRow } = require('commonsComponents/OKTableRow');
const { OKTableCell } = require('commonsComponents/OKTableCell');
const OKTooltip = require('components/commons/OKTooltip/OKTooltip');

// @helpers
const { getLanguage, formatText, FormattedText } = require('core/i18n').i18n;
const DateHelper = require('utilities/dateHelper');
const { getProductDescriptionById } = require('utilities/productDescription');
const { getLastNChars } = require('utilities/stringHelper');
const { sendTextToClipBoard } = require('commonsUtilities/clipBoardHelper');
const { formatWithoutRound, formatCurrency, formatAmount } = require('utilities/currencyHelper');
const { valueOrHyphen, conditionalValueOrHyphen } = require('utilities/valueHelper');

// @constants
const { DATE_FORMAT_DAY_MONTH_SHORT, DATE_FORMAT_ES, HYPHEN, BROKER_TYPE_RIGHT_NEW_DECIMALS } = require('constants/index');

class BrokerMyInvestmentsProductsOthers extends React.Component {
    constructor(props) {
        super(props);

        this.buildTooltipContent = this.buildTooltipContent.bind(this);
        this.buildContractNumber = this.buildContractNumber.bind(this);
        this.copyIbanToClipboard = this.copyIbanToClipboard.bind(this);
    }

    buildAmountCurrency(amount, currency) {
        if (amount===HYPHEN){
            return <span className="broker-products-table-row__amount">{amount}</span>
        } else {
            return (
                <span>
                    <span className="broker-products-table-row__amount">{amount} </span>
                    <span className="broker-products-table-row__currency">{currency}</span>
                </span>
            );
        }
    }

    buildContractNumber(immStock) {
        if (this.props.isContractSection || !immStock) {
            return null;
        }

        const contractForTooltip = immStock.get('contractForTooltip');
        const tooltipContent = this.buildTooltipContent(contractForTooltip);
        const textAndCroppedContractNumber = (
            <span className="broker-products-table-others__row-contract-number-label">
                <span className="broker-products-table-row__contract-number-label">
                    {formatText('broker-myInvestmentsWrappedProductsTableContractLabel')}
                </span>
                <span className="broker-products-table-row__contract-number-value">
                    {getLastNChars(contractForTooltip)}
                </span>
            </span>
        );

        return (
            <div className="broker-products-table-row__contract-number">
                <OKTooltip
                    target={textAndCroppedContractNumber}
                    content={tooltipContent}
                    offset={[34, -10]}
                    type="descriptive"
                />
            </div>
        );
    }

    buildFormattedDateAndTime(immStock, index) {
        const quoteTime = valueOrHyphen(immStock.getIn([index, 'quoteTime']));
        const date = immStock.getIn([index, 'quoteDate']);
        const quoteOriginalDate = DateHelper.buildFormattedDate(date, DATE_FORMAT_ES);
        let quoteDate = conditionalValueOrHyphen(date!==null, DateHelper.buildFormattedDate(date, DATE_FORMAT_DAY_MONTH_SHORT));

        if (moment().isSame(quoteOriginalDate, 'day')) {
            quoteDate = formatText('broker-todayMyInvestmentsProductsTable');
        }

        if (quoteDate===HYPHEN && quoteTime===HYPHEN){
            return HYPHEN;
        }

        return quoteDate + ', ' + quoteTime;
    }

    buildHeader() {
        return (
            <thead>
                <OKTableRow asHeader={true}>
                    <OKTableCell
                        asHeader={true}
                        className="broker-products-table-others__name"
                        width="30%"
                    >
                        <FormattedText value="broker-productNameOthers"/>
                    </OKTableCell>
                    <OKTableCell
                        alignment="right"
                        asHeader={true}
                        className="broker-products-table-others__titles"
                        width="5%"
                    >
                        <FormattedText value="broker-productSharesStocks"/>
                    </OKTableCell>
                    <OKTableCell
                        alignment="right"
                        asHeader={true}
                        className="broker-products-table-others__quote"
                        width="14%"
                    >
                        <FormattedText value="broker-productNATStocks"/>
                    </OKTableCell>
                    <OKTableCell
                        alignment="right"
                        asHeader={true}
                        className="broker-products-table-others__actual-value"
                        width="14%"
                    >
                        <FormattedText value="broker-productSharesActualValue"/>
                    </OKTableCell>
                    <OKTableCell
                        alignment="right"
                        asHeader={true}
                        className="broker-products-table-others__description"
                        width="22%"
                    >
                        <FormattedText value="broker-productDescriptionOthers"/>
                    </OKTableCell>
                    <OKTableCell
                        alignment="right"
                        asHeader={true}
                        className="broker-products-table-others__in-wallet"
                        width="15%"
                    >
                        <FormattedText value="broker-productInWallet" />
                    </OKTableCell>
                </OKTableRow>
            </thead>
        );
    }

    buildQuotationLabel(immStock) {
        const quotationValue = immStock.get('quotationValue');

        if (quotationValue != null) {
            const quotationFormatted = formatAmount(quotationValue, getLanguage(), BROKER_TYPE_RIGHT_NEW_DECIMALS);
            return quotationFormatted;
        } else {
            return HYPHEN;
        }
    }

    buildRows() {
        const { showCompleteList, immStocks } = this.props;
        const immStocksToDisplay = showCompleteList ? immStocks : immStocks.slice(0, 5);

        const rows = immStocksToDisplay.map((immStock, index) => {
            const name = immStock.get('name');
            const percentageInWallet = immStock.get('percentageInWallet');
            const formattedPercentageInWallet = percentageInWallet < 1 ? '< 1' : Math.round(percentageInWallet);
            const isin = valueOrHyphen(immStock.get('isin'));
            const marketName = valueOrHyphen(immStock.get('shortMarketName'));
            const currencyText = formatCurrency(immStock.get('quotationCurrency'));
            const priceLabel = this.buildQuotationLabel(immStock);
            const titles = valueOrHyphen(immStock.get('shares'));
            const description = getProductDescriptionById(immStock.get('tipoValorEmision'));
            const dateAndTime = immStock.get('quotationValue') ? this.buildFormattedDateAndTime(immStocks, index) : null;
            const totalValue = immStock.get('totalValue');
            const value = conditionalValueOrHyphen(totalValue!==null, formatWithoutRound(totalValue, 2));
            const valueCurrency = immStock.get('totalValueCurrency');
            const formattedValue = this.buildAmountCurrency(value, valueCurrency);

            return (
                <OKTableRow className="broker-products-table-others__row" key={index}>
                    <OKTableCell>
                        <div className="broker-products-table-others__row-product-name">
                            {name}
                        </div>
                        <div className="broker-products-table-others__row-market-isin">
                            {marketName} | <FormattedText value="broker-isinLabel" injectedStrings={[isin]}/>
                        </div>
                        {this.buildContractNumber(immStock)}
                    </OKTableCell>
                    <OKTableCell className="broker-products-table-others__row-titles broker-products-table-row__cell-vertical-align--center">
                        {titles}
                    </OKTableCell>
                    <OKTableCell className="broker-products-table-others__row-quote broker-products-table-row__cell-vertical-align--center">
                        <div className="broker-products-table-others__row-quote-label">
                            {priceLabel} {currencyText}
                        </div>
                        <div className="broker-products-table-others__row-quote-date-time-label">
                            {dateAndTime}
                        </div>
                    </OKTableCell>
                    <OKTableCell className="broker-products-table-others__row-actual-value broker-products-table-row__cell-vertical-align--center">
                        {formattedValue}
                    </OKTableCell>
                    <OKTableCell className="broker-products-table-others__row-description broker-products-table-row__cell-vertical-align--center">
                        {description}
                    </OKTableCell>
                    <OKTableCell className="broker-products-table-others__row-in-wallet broker-products-table-row__cell-vertical-align--center">
                        {formattedPercentageInWallet}%
                    </OKTableCell>
                </OKTableRow>
            );
        });

        return (
            <tbody>
                {rows}
            </tbody>
        );
    }

    buildTooltipContent(iban) {
        return (
            <span>
                {iban}
                <button
                    className="icon__tooltip icon-copiar"
                    onClick={() => this.copyIbanToClipboard(iban)}
                >
                </button>
            </span>
        );
    }

    copyIbanToClipboard(iban) {
        sendTextToClipBoard(iban);
    }

    render() {
        const header = this.buildHeader();
        const rows = this.buildRows();

        return (
            <OKTable className="broker-products-table-others">
                {header}
                {rows}
            </OKTable>
        );
    }
}

BrokerMyInvestmentsProductsOthers.propTypes = {
    immStocks: PropTypes.object,
    showCompleteList: PropTypes.bool,
    isContractSection: PropTypes.bool
};

BrokerMyInvestmentsProductsOthers.defaultProps = {
    immStocks: Immutable.List(),
    showCompleteList: false,
    isContractSection: false
};

module.exports = BrokerMyInvestmentsProductsOthers;
